import { useSignalRNotifications } from './useSignalRNotifications';
import { ComparateurPricingUpdateDto } from '../types';

/**
 * Configuration for comparateur pricing notifications
 */
export const COMPARATEUR_NOTIFICATIONS_CONFIG = {
  route: '/signalr/comparateur',
  joinMethod: 'JoinSession',
  leaveMethod: 'LeaveSession',
  eventName: 'PricingUpdate'
} as const;

/**
 * Hook for comparateur pricing notifications via SignalR
 *
 * @param borrowerId - Borrower ID used as SignalR group ID
 * @param onPricingUpdate - Callback when a pricing update is received
 *
 * @example
 * ```tsx
 * const { isConnected, memoizedConnect, memoizedDisconnect } =
 *   useComparateurNotifications(borrowerId, (update) => {
 *     // Update the contract funnel matching update.oldHash
 *     dispatch({ type: 'UPDATE_FUNNEL_PRICING', payload: update });
 *   });
 *
 * useEffect(() => {
 *   if (borrowerId) {
 *     memoizedConnect();
 *   }
 *   return memoizedDisconnect;
 * }, [borrowerId, memoizedConnect, memoizedDisconnect]);
 * ```
 */
export const useComparateurNotifications = (
  borrowerId?: string | null,
  onPricingUpdate?: (update: ComparateurPricingUpdateDto) => void
) => {
  const {
    connection,
    isConnected,
    error,
    memoizedConnect,
    memoizedDisconnect,
    notifications,
    latestNotification,
    clearNotifications
  } = useSignalRNotifications<ComparateurPricingUpdateDto>({
    ...COMPARATEUR_NOTIFICATIONS_CONFIG,
    groupId: borrowerId || undefined,
    onNotification: onPricingUpdate
  });

  return {
    // Connection state
    connection,
    isConnected,
    error,

    // Connection methods
    memoizedConnect,
    memoizedDisconnect,

    // Notification data
    notifications,
    latestNotification,

    // Utility methods
    clearNotifications
  };
};
